import React, { useState, useEffect } from 'react';
import { Crown, CreditCard, Check, ArrowRight, Shield, Users, Calendar, Star } from 'lucide-react';

const SubscriptionGate = ({ user, onSubscribed }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const isAthlete = user?.plan === 'athlete';
  const planPrice = isAthlete ? '$1' : '$2';
  const planFeatures = isAthlete
    ? [
        { icon: Star,     text: 'Personal Training Log',  color: '#2563eb' },
        { icon: Calendar, text: 'Schedule Management',    color: '#059669' },
        { icon: Star,     text: 'Personal Best Tracking', color: '#f59e0b' },
        { icon: Shield,   text: 'Workout Templates',      color: '#dc2626' },
      ]
    : [
        { icon: Users,    text: 'Unlimited Athletes',     color: '#2563eb' },
        { icon: Calendar, text: 'Schedule Management',    color: '#059669' },
        { icon: Star,     text: 'Personal Best Tracking', color: '#f59e0b' },
        { icon: Shield,   text: 'Workout Templates',      color: '#dc2626' },
      ];

  useEffect(() => {
    // Check if user already has active subscription
    checkSubscriptionStatus();
  }, [user]);

  const checkSubscriptionStatus = async () => {
    if (!user) return;
    
    setCheckingStatus(true);
    try {
      // Check if user has subscription_status field
      if (user.subscription_status === 'active') {
        onSubscribed();
        return;
      }
      
      // If no status, they need to subscribe
      setCheckingStatus(false);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setCheckingStatus(false);
    }
  };

  const handleStartSubscription = async () => {
    setIsLoading(true);
    
    try {
      // Call your API to create Stripe checkout session
      const response = await fetch('https://coach-log-api.vercel.app/api/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email,
          plan: user.plan || 'coach',
          successUrl:window.location.origin + '?success=true&session_id={CHECKOUT_SESSION_ID}',
          cancelUrl: window.location.origin + '/dashboard'
        })
      });

      const { sessionId } = await response.json();
      
      // Redirect to Stripe Checkout
      const stripe = window.Stripe('pk_live_51TGl5l0ZCjEKNHzbxTUx6adHHCMJjHrbw90HC5WbLRhRoM5rWwtFmjejbXos8bQrkofT9dyAPBYPk2p3pwRyxbdb00OxBrvvg1'); // Replace with your key
      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionId
      });

      if (error) {
        console.error('Stripe error:', error);
        alert('Payment setup failed. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '48px',
          textAlign: 'center',
          maxWidth: '400px',
          width: '100%',
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: '4px solid #e2e8f0',
            borderTopColor: '#2563eb',
            margin: '0 auto 24px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 8px 0'
          }}>
            Checking your subscription...
          </h2>
          <p style={{
            color: '#64748b',
            fontSize: '14px',
            margin: 0
          }}>
            Please wait a moment
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '48px',
        maxWidth: '600px',
        width: '100%',
        border: '1px solid #e2e8f0',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <img 
              src="/coach-logo.png" 
              alt="Coach's Log" 
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px'
              }}
            />
            <h1 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: '#1e293b',
              margin: 0
            }}>
              <span style={{ color: '#2563eb' }}>Coach's</span> <span style={{ color: '#1e293b' }}>Log</span>
            </h1>
          </div>
          
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 8px 0'
          }}>
            Welcome to Coach's Log
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#64748b',
            margin: 0,
            fontWeight: '500'
          }}>
            Start free for 7 days, then just {planPrice}/month
          </p>
        </div>

        {/* Features */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '20px',
          marginBottom: '32px'
        }}>
          {planFeatures.map((feature, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <feature.icon size={20} style={{ color: feature.color }} />
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1e293b'
              }}>
                {feature.text}
              </span>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <span style={{
              fontSize: '48px',
              fontWeight: '800',
              color: '#1e293b'
            }}>
              7 days free
</span>
            <span style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#64748b'
            }}>
              then {planPrice}/month
            </span>
          </div>
          <p style={{
            fontSize: '14px',
            color: '#64748b',
            margin: 0,
            fontWeight: '500'
          }}>
            No Charge for 7 days • Cancel anytime • No setup fees • Secure billing
          </p>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleStartSubscription}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '16px',
            background: isLoading 
              ? '#9ca3af' 
              : 'linear-gradient(135deg, #2563eb, #3b82f6)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontWeight: '700',
            fontSize: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }
          }}
        >
          {isLoading ? (
            <>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: 'white',
                animation: 'spin 1s linear infinite'
              }}></div>
              Setting up...
            </>
          ) : (
            <>
              <CreditCard size={20} />
              Start My Free Trial
              <ArrowRight size={20} />
            </>
          )}
        </button>

        {/* Trust Signals */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginTop: '24px',
          padding: '16px',
          background: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Shield size={16} style={{ color: '#059669' }} />
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#064e3b'
            }}>
              Secure
            </span>
          </div>
          <div style={{
            width: '1px',
            height: '16px',
            background: '#e2e8f0'
          }}></div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Check size={16} style={{ color: '#059669' }} />
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#064e3b'
            }}>
              Cancel Anytime
            </span>
          </div>
          <div style={{
            width: '1px',
            height: '16px',
            background: '#e2e8f0'
          }}></div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Crown size={16} style={{ color: '#f59e0b' }} />
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#92400e'
            }}>
              Professional
            </span>
          </div>
        </div>

        {/* Footer Note */}
        <p style={{
          textAlign: 'center',
          fontSize: '12px',
          color: '#9ca3af',
          margin: '24px 0 0 0',
          lineHeight: '1.5'
        }}>
          By subscribing, you agree to our Terms of Service and Privacy Policy.<br />
          Powered by Stripe for secure payment processing.
        </p>
      </div>

      {/* Add CSS animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionGate;